// Tenant data export and account closure (ADR 0062, docs/specs/tenant-export-and-closure.md
// section 3). Mirrors crates/api-types/src/closure.rs. closureFixtures.json holds one value of
// each type; the Rust tests round-trip each through its type, and closure.test.ts checks every
// enum value in it against the lists here, so the two sides can't drift.
//
// Every route is for company admins only (403 `admin_only`) and stays reachable while the
// company is suspended or closing.
import type { ApiClient } from "./client.ts";
import type { Timestamp, Uuid } from "./types.ts";

/** Export requests a company may make in 24 hours (429 `export_limit` after). */
export const EXPORTS_PER_DAY = 3;
/** Days a ready export is kept. */
export const EXPORT_KEEP_DAYS = 7;
/** Days between a closure and deletion. */
export const CLOSURE_GRACE_DAYS = 30;
/** Admin names `MeClosure.admin_names` carries at most. */
export const CLOSURE_ADMIN_NAMES_MAX = 3;

export const EXPORT_STATES = ["queued", "building", "ready", "failed", "expired"] as const;
export type ExportState = (typeof EXPORT_STATES)[number];

export const CLOSURE_STATES = ["grace", "canceled", "purging", "purged"] as const;
export type ClosureState = (typeof CLOSURE_STATES)[number];

export const CLOSURE_REASONS = ["admin", "trial_expired", "staff"] as const;
export type ClosureReason = (typeof CLOSURE_REASONS)[number];

export const CLOSURE_CANCEL_SOURCES = ["admin", "staff", "plan"] as const;
export type ClosureCancelSource = (typeof CLOSURE_CANCEL_SOURCES)[number];

/** Error codes the export and closure routes answer with. */
export const CLOSURE_ERRORS = [
  "admin_only",
  "export_running",
  "export_limit",
  "export_expired",
  "closure_open",
  "closure_purging",
  "name_mismatch",
  "reauth_required",
] as const;
export type ClosureError = (typeof CLOSURE_ERRORS)[number];

/** Queued or building: at most one per company (409 `export_running`). */
export function isExportRunning(state: ExportState): boolean {
  return state === "queued" || state === "building";
}

/** One file of a ready export. The storage key never leaves the server. */
export interface ExportFile {
  name: string;
  bytes: number;
  sha256: string | null;
}

/** A data export: one row of the Exports card. */
export interface Export {
  id: Uuid;
  state: ExportState;
  include_files: boolean;
  requested_at: Timestamp;
  /** Null once the person who asked has been removed. */
  requested_by_name: string | null;
  /** 0 to 100 while building; null otherwise. */
  percent: number | null;
  /** The archive's files once ready; empty before and after expiry. */
  files: ExportFile[];
  bytes_total: number | null;
  ready_at: Timestamp | null;
  /** `ready_at` plus 7 days. */
  expires_at: Timestamp | null;
  failed_reason: string | null;
}

/** `GET /v1/admin/exports`: newest first, 25 to a page. */
export interface ExportList {
  items: Export[];
  /** Pass as `cursor` for Load more; null on the last page. */
  next_cursor: string | null;
}

export interface NewExport {
  /** Recordings and files in `files-N.zip` parts (the dialog's switch, on by default). */
  include_files: boolean;
}

/** The presigned download link, valid 5 minutes. */
export interface ExportFileUrl {
  url: string;
  expires_at: Timestamp;
}

/** The company's closure, for admins. */
export interface Closure {
  id: Uuid;
  state: ClosureState;
  reason: ClosureReason;
  /** The company's name when the closure opened. */
  company_name: string;
  requested_at: Timestamp;
  /** "Closed by Dana Whitfield". Null for a trial's own closure or a removed person. */
  requested_by_name: string | null;
  /** When deletion starts. Never changes. */
  purge_after: Timestamp;
  /** Keep account is open to this admin now. */
  can_cancel: boolean;
  /** Keep account leaves a self-serve company without a plan: open Billing's Choose plan. */
  needs_plan: boolean;
  canceled_at: Timestamp | null;
  canceled_by_name: string | null;
  cancel_source: ClosureCancelSource | null;
}

export interface CloseAccount {
  /** The company's name, typed exactly (trimmed); else 422 `name_mismatch`. */
  confirm_name: string;
}

/** `GET /v1/admin/closure/preview`: what the close dialog states before the admin confirms. */
export interface ClosurePreview {
  /** The name to type, and the dialog's title. */
  company_name: string;
  /** When everything would be deleted if the admin closed now (today plus 30 days). */
  purge_after: Timestamp;
  /** Show "The plan is canceled today." */
  plan_canceled: boolean;
  /** Show "Incoming number transfers are canceled at deletion." */
  port_ins_canceled: boolean;
  /** Continue leads to "Confirm it's you" first. */
  reauth_needed: boolean;
}

/** `Me.closure`: present while the company is closing (grace or purging), to every member. */
export interface MeClosure {
  state: ClosureState;
  purge_after: Timestamp;
  reason: ClosureReason;
  requested_by_name: string | null;
  /** This member is an admin and the closure is in grace. */
  can_cancel: boolean;
  /** The members' closing screen: at most 3 names. */
  admin_names: string[];
  /** Admins beyond `admin_names` ("and 2 more"). */
  more_admins: number;
}

/** `company.closure_changed`: reload `/v1/me` and show or clear the closing strip. */
export interface ClosureChanged {
  state: ClosureState;
  purge_after: Timestamp;
}

/** `export.changed`, to admins. */
export interface ExportChanged {
  id: Uuid;
  state: ExportState;
  percent: number | null;
}

// The ops console (`/v1/platform/companies/{company}/closure/*`, the ops host only).

export interface OpsClosureCancel {
  note: string;
}

export interface OpsLegalHold {
  on: boolean;
  note: string;
}

export interface OpsClosure {
  id: Uuid;
  tenant_id: Uuid;
  state: ClosureState;
  reason: ClosureReason;
  company_name: string;
  requested_at: Timestamp;
  requested_by_email: string | null;
  purge_after: Timestamp;
  legal_hold: boolean;
  legal_hold_note: string | null;
  canceled_at: Timestamp | null;
  cancel_source: ClosureCancelSource | null;
  purge_started_at: Timestamp | null;
  purged_at: Timestamp | null;
  /** Finished purge steps, in order. */
  steps_done: string[];
}

const enc = encodeURIComponent;

/** Admin > Data: exports and closure. */
export class ClosureApi {
  constructor(private readonly client: ApiClient) {}

  exports(cursor?: string | null, signal?: AbortSignal) {
    const q = cursor ? `?cursor=${enc(cursor)}` : "";
    return this.client.get<ExportList>(`/v1/admin/exports${q}`, signal);
  }
  /** 202 with the new export, Queued. */
  requestExport(body: NewExport) {
    return this.client.post<Export>("/v1/admin/exports", body);
  }
  /**
   * The presigned link of one file (valid 5 minutes), through the JSON form so a browser can
   * read it without following a cross-origin redirect.
   */
  exportFileUrl(id: Uuid, name: string) {
    return this.client.get<ExportFileUrl>(
      `/v1/admin/exports/${enc(id)}/files/${enc(name)}?format=json`,
    );
  }
  /** The company's open or latest closure, or null. */
  closure(signal?: AbortSignal) {
    return this.client.get<Closure | null>("/v1/admin/closure", signal);
  }
  /** The close dialog's lines and whether it must ask for a recent sign-in first. */
  closurePreview(signal?: AbortSignal) {
    return this.client.get<ClosurePreview>("/v1/admin/closure/preview", signal);
  }
  /** 201 with the closure in grace; 403 `reauth_required` asks for a recent sign-in. */
  close(body: CloseAccount) {
    return this.client.post<Closure>("/v1/admin/closure", body);
  }
  /** Keep account: the closure, canceled. */
  keep() {
    return this.client.del<Closure>("/v1/admin/closure");
  }
}
